import { useCallback, useEffect, useState, useRef } from 'react';
import style from './style.module.scss';
import MessageBoxSet from '../message-box-set';
import { useStore } from '../../lib/hooks/useStore';
import { getCardPaymentImage } from '../../lib/integrations';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import Confirm from '../confirm';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import logger from '../../lib/helpers/logger';
import BalancesKvpList from '../balances-kvp-list';
import { PaymentCardServerProps } from '../../lib/typegen/payment-card-server-props';
import api from '../../lib/api/api';
import { v4 as guid } from 'uuid';
import EMVDevice, {
	KioEMVProductDescriptor,
	KioEMVTransactionParams,
	KioEMVResult,
	BillingInfo,
	CreateCartGSParams,
} from '../../lib/devices/emv-device/EMVDevice';
import { AppEnvironment } from '../../lib/typegen/app-environment';
import MessageBox from '../message-box';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import ServiceFeeView from '../service-fee-view';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import enumHelper from '../../lib/helpers/enums';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';
import { PaymentType } from '../../lib/typegen/payment-type';
import { translateCardReaderText } from '../../lib/helpers/translateCardReaderText';

function PaymentCard({
	imageAltKey,
	paymentFeeViewServerProps,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	cardErrorGenericLabelKey,
	processingPaymentLabelKey,
	tryAgainMessageLabelKey,
	tryAgainNoLabelKey,
	tryAgainYesLabelKey,
}: PaymentCardServerProps) {
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.SERVICE_FEE);
	const [error, setError] = useState<PaymentError | null>(null);
	const [errorHint, setErrorHint] = useState<string | null>(null);
	const emvDeviceRef = useRef<EMVDevice | null>(null);
	const [pendingCartInfo, setPendingCartInfo] = useState<{ cartId: string; token: string } | null>(null);
	const hasConfirmedFeeRef = useRef(false);
	const PAYMENT_CARD_REGISTRY_KEY = 'payment-card';

	const {
		accountStore: {
			cartItems,
			cartTotalAmount,
			cartId,
			setCartId,
			setToken,
			transactionFee,
			setTransactionFee,
			transactionTotalWithFee,
			setTransactionTotalWithFee,
			payorInformation,
			cardType,
			setPaymentType,
		},
		sessionStore: { legacyId },
		kioskStore: { kioskId, companyId },
		uiStore: { handleNavigation, clearCustomBackAction, showLoader, hideLoader },
		configStore: { getString, environment, config, kioskLocation, cisType, ppType, currentLanguage, autoTranslate, setTranslation },
	} = useStore();

	useEffect(() => setPaymentType(cardType === 1 ? PaymentType.DebitCard : PaymentType.CreditCard), [setPaymentType, cardType]);

	const runEMVSale = useCallback(
		(cartId: string, _token: string, amount: number) => {
			if (!emvDeviceRef.current) {
				logger.logGeneral.error({}, 'EMV device not initialized', 'The EMV device was not able to be initialized.');
				setError({ message: getString(cardErrorGenericLabelKey) || 'Device not ready' });
				setCurrentState(ViewState.ERROR);
				return;
			}

			const saleParams: KioEMVTransactionParams = {
				DeviceName: 'EMV',
				PaymentAmount: Math.round(amount * 100), // Convert to cents
				TransactionRef: cartId,
				TokenType: cardType ?? 0, // 0 for credit, 1 for debit
			};

			const saleStarted = emvDeviceRef.current.runSale(saleParams);
			if (!saleStarted) {
				logger.logGeneral.error({}, 'Failed to start EMV sale', 'The EMV device was unable to run the Sale command.');
				setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to start payment' });
				setCurrentState(ViewState.ERROR);
			}
		},
		[getString, cardErrorGenericLabelKey, cardType]
	);

	// Initialize EMV device
	useEffect(() => {
		if (!emvDeviceRef.current) {
			emvDeviceRef.current = new EMVDevice('EMV');
		}

		// Set up window callback for cart creation
		window.OnCreateCartGS = (res: string, isErr: boolean, errCode: number) => {
			console.log('OnCreateCartGS callback:', JSON.stringify({ res, isErr, errCode }));

			if (isErr || errCode !== 0) {
				logger.logGeneral.error({}, 'Failed to create cart:', 'OnCreateCartGS', { errCode, res, isErr });
				setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to initialize payment' });
				setCurrentState(ViewState.ERROR);
				return;
			}

			try {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const cartInfoRaw: any = JSON.parse(res);
				logger.logGeneral.log({}, 'Grant Street Cart created successfully:', cartInfoRaw);

				const fees = cartInfoRaw.Fees;
				const feeAmount =
					cardType === 1
						? config.debitServiceFee || fees?.DebitCard?.fees || '0'
						: config.creditServiceFee || fees?.CreditCard?.fees || '0';
				const totalAmount = cardType === 1 ? fees?.DebitCard?.total || '0' : fees?.CreditCard?.total || '0';

				console.log('Fee extraction:', { cardType, feeAmount, totalAmount, fees });

				setTransactionFee(feeAmount);
				setTransactionTotalWithFee(totalAmount);

				// Store cart info
				setCartId(cartInfoRaw.Id);
				setToken(cartInfoRaw.Token);

				// Store cart info for later use when service fee is confirmed
				setPendingCartInfo({ cartId: cartInfoRaw.Id, token: cartInfoRaw.Token });

				// Skip service fee view if fee is 0 and go directly to PAY state
				const feeValue = parseFloat(feeAmount);
				console.log('Checking if should skip service fee:', {
					feeValue,
					isNaN: isNaN(feeValue),
					willSkip: feeValue === 0 || isNaN(feeValue),
				});

				if (feeValue === 0 || isNaN(feeValue)) {
					console.log('Skipping service fee view, going directly to PAY');
					setCurrentState(ViewState.PAY);
					// Run sale immediately if there's no fee to confirm
					runEMVSale(cartInfoRaw.Id, cartInfoRaw.Token, cartTotalAmount);
				} else {
					console.log('Service fee exists, showing SERVICE_FEE view');
					// Show service fee view to the user
					setCurrentState(ViewState.SERVICE_FEE);
				}
			} catch (error) {
				logger.logGeneral.error({}, 'Failed to parse cart response:', 'OnCreateCartGS', error);
				setError({ message: getString(cardErrorGenericLabelKey) || 'Payment initialization error' });
				setCurrentState(ViewState.ERROR);
			}
		};

		// Set up window callback for transaction completion
		window.OnKioEMVTransactionCompleted = async (result: KioEMVResult) => {
			console.log('OnKioEMVTransactionCompleted callback:', JSON.stringify({ result }));

			// Set to processing state when transaction starts
			setCurrentState(ViewState.PROCESSING);
			const item = cartItems[0];
			if (!item.customer) item.customer = { customerId: cartId || '', name: `${payorInformation?.firstName} ${payorInformation?.lastName}` };

			if (result.Result === 0) {
				// Success
				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item!.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: cartTotalAmount!,
					paymentType: 'Card',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: item!.id!,
					payorName: item!.customer!.name!,
				});

				api.legacyBpkAdmin.payments.postAcceptedPayment(legacyPostAcceptedPayment).catch((error) => {
					logger.logGeneral.error({}, 'Failed to post accepted payment:', error);
				});

				logger.logGeneral.log({}, 'Payment successful', {
					referenceId: result.ReferenceID,
					authCode: result.AuthorizationCode,
					amount: result.Amount,
					cardType: result.CardType,
				});
				handleNavigation('payment-complete');
			} else {
				// Error
				const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					billpaySessionGUID: legacyId!,
					rejectedECID: '',
					rejectedReason: 'Card payment error',
					rejectedPaymentType: 'Card',
					paymentAmount: cartTotalAmount!,
					payorName: item!.customer!.name!,
					paymentDateTime: new Date().toISOString(),
					passKey: await passKeyHelper.generatePassKey(),
				});

				api.legacyBpkAdmin.payments.postRejectedPayment(legacyPostRejectedPayment).catch((error) => {
					logger.logGeneral.error({}, 'Failed to post rejected payment:', error);
				});

				logger.logGeneral.error({}, 'Payment failed:', {
					errors: result.Errors,
					errorCode: result.ErrorCode,
				});
				const readerError = result.Errors?.trim();
				setError({
					message: readerError
						? await translateCardReaderText(readerError, { getString, currentLanguage, autoTranslate, setTranslation })
						: getString(cardErrorGenericLabelKey),
				});
				setErrorHint(getString('card-read-hint'));
				setCurrentState(ViewState.ERROR);
			}
		};
	}, [
		cardType,
		setTransactionFee,
		setCartId,
		setToken,
		runEMVSale,
		cartTotalAmount,
		getString,
		currentLanguage,
		autoTranslate,
		setTranslation,
		cardErrorGenericLabelKey,
		handleNavigation,
		config.debitServiceFee,
		config.creditServiceFee,
		setTransactionTotalWithFee,
		cartItems,
		cartId,
		payorInformation?.firstName,
		payorInformation?.lastName,
		legacyId,
		companyId,
		kioskId,
		transactionFee,
		cisType,
		ppType,
	]);

	useSessionCleanup(
		PAYMENT_CARD_REGISTRY_KEY,
		useCallback(async () => {
			logger.logGeneral.warn({}, 'Cleaning up incomplete EMV payment session');

			try {
				emvDeviceRef.current?.cancelTransaction();
			} catch (error) {
				logger.debug.error('Error cancelling EMV Device during cleanup', error);
			}

			// Clear the fee confirmation flag on cleanup
			sessionStorage.removeItem('payment-card-fee-confirmed');
		}, [])
	);

	// Call prepareTransaction when component mounts
	useEffect(() => {
		showLoader();
		sessionStorage.removeItem('payment-card-fee-confirmed');

		const initializePayment = async () => {
			// Skip initialization in demo mode - user will manually trigger processing
			if (environment === AppEnvironment.Demo) {
				logger.logGeneral.log({}, 'Demo mode: Skipping automatic payment initialization');
				return;
			}

			if (!payorInformation) {
				logger.logGeneral.error({}, 'Payor information is missing', 'initializePayment');
				setError({ message: getString(cardErrorGenericLabelKey) || 'Payment information missing' });
				setCurrentState(ViewState.ERROR);
				return;
			}

			const prepareTransactionRequest = {
				accountsToPay: cartItems.map((item) => ({
					accountNumber: item.item.itemNumber,
					amount: item.amountToPay,
					taxYear: item.taxYear || undefined,
				})),
				paymentType: cardType === 1 ? 'DEBIT' : 'CREDIT',
				location: kioskLocation,
				totalAmount: cartTotalAmount,
				payorInfo: payorInformation,
			};

			try {
				// Get transaction ID
				console.log('Calling getTransactionId API...');
				const transactionId = await api.lookups.getTransactionId(prepareTransactionRequest);
				if (!transactionId) {
					throw new Error('Failed to get transaction ID');
				}
				console.log('Transaction ID received:', transactionId);

				// Create cart with KioEMVDevice
				if (!emvDeviceRef.current) return;

				const productDescriptors: KioEMVProductDescriptor[] = cartItems.map((item, index) => ({
					SKU: item.id || `item-${index}`,
					Description: `${item.item.itemNumber.replace('-', ' ')}`,
					Quantity: 1,
					TotalAmount: Math.round(item.amountToPayWithFee * 100),
					Department: 'TAC',
					Category: 'Property Tax',
					Type: 'Property Tax Bill',
				}));

				const billingInfo: BillingInfo = {
					name: `${payorInformation.firstName} ${payorInformation.lastName}`,
					email: payorInformation.emailAddress,
					phone: payorInformation.phoneNumber,
					address: payorInformation.street,
					address2: '',
					city: payorInformation.city,
					state: payorInformation.state,
					zip: payorInformation.zip.toString(),
					country: 'US',
				};

				const params: CreateCartGSParams = {
					billingInfo,
					orderId: transactionId,
					productDescriptors,
					devName: 'EMV',
				};

				console.log('Creating cart with Grant Street...', JSON.stringify(params));
				emvDeviceRef.current.createCartGS(params);
			} catch (error) {
				console.error('Failed to initialize payment:', error);
				setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to initialize payment' });
				setCurrentState(ViewState.ERROR);
			}
			hideLoader();
		};

		initializePayment();
		// Only run once on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleRetry = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
	};

	const handleCancel = () => {
		setError(null);
		handleNavigation('payment-method');
	};

	const handleServiceFeeConfirm = () => {
		console.log('handleServiceFeeConfirm called - setting state to PAY');
		// Store confirmation in sessionStorage to persist across remounts
		sessionStorage.setItem('payment-card-fee-confirmed', 'true');
		hasConfirmedFeeRef.current = true;
		// Set state to PAY first
		setCurrentState(ViewState.PAY);
	};

	const handleServiceFeeCancel = () => {
		handleNavigation('payment-method');
	};

	// Run EMV sale when PAY view is shown (after service fee confirmation or when no fee)
	useEffect(() => {
		console.log('EMV sale useEffect triggered:', { currentState, hasPendingCartInfo: !!pendingCartInfo, hasError: !!error });
		if (currentState === ViewState.PAY && pendingCartInfo && !error) {
			// Use a small delay to ensure the PAY view renders before starting EMV sale
			const timer = setTimeout(() => {
				console.log('Starting EMV sale from PAY state');
				runEMVSale(pendingCartInfo.cartId, pendingCartInfo.token, cartTotalAmount);
			}, 100);

			return () => clearTimeout(timer);
		}
	}, [currentState, pendingCartInfo, error, runEMVSale, cartTotalAmount]);

	// Demo mode simulation effect
	useEffect(() => {
		if (environment === AppEnvironment.Demo && currentState === ViewState.PAY) {
			// Wait 5 seconds showing the PAY view
			const showPayTimer = setTimeout(() => {
				logger.logGeneral.log({}, 'Demo mode: Starting payment processing simulation');
				setCurrentState(ViewState.PROCESSING);

				// Then wait 10 seconds in processing before completing
				const processingTimer = setTimeout(() => {
					logger.logGeneral.log({}, 'Demo mode: Payment simulation complete');
					handleNavigation('payment-complete');
				}, 10000);

				return () => clearTimeout(processingTimer);
			}, 5000);

			return () => clearTimeout(showPayTimer);
		}
	}, [environment, currentState, handleNavigation]);

	const renderServiceFeeView = () => {
		// Show a loader while waiting for fee data to load
		if (!transactionFee || !transactionTotalWithFee) {
			return (
				<section className={style.processing}>
					<div className={style.processing__spinner}></div>
					<h2 className={style.processing__title}>Loading...</h2>
				</section>
			);
		}

		const feeValue = parseFloat(transactionFee || '0');
		const amount = cartTotalAmount;

		return (
			<ServiceFeeView
				amount={amount}
				fee={feeValue}
				onConfirm={handleServiceFeeConfirm}
				onCancel={handleServiceFeeCancel}
				labels={
					paymentFeeViewServerProps
						? paymentFeeViewServerProps
						: ({ titleLabelKey: 'service-fee-description-card' } as PaymentFeeViewServerProps)
				}
			/>
		);
	};

	const renderPayView = () => {
		const item = cartItems[0];
		if (!item.customer) item.customer = { customerId: cartId || '', name: `${payorInformation?.firstName} ${payorInformation?.lastName}` };

		//const feeValue = transactionFee ? parseFloat(transactionFee) : 0;
		//const safeFeeValue = isNaN(feeValue) ? 0 : feeValue;

		return (
			<section className={style.section}>
				<img className={style.section__image} src={getCardPaymentImage(cisType)} alt={imageAltKey} />
				<BalancesKvpList
					className={style.section__details}
					kvpList={[{ label: getString(paymentTotalLabelKey ?? ''), value: parseFloat(transactionTotalWithFee || '0') }]}
				/>
				<MessageBoxSet
					className={style.section__messages}
					infoText={getString(infoBoxLabelKey ?? '')}
					warningText={getString(warningBoxLabelKey ?? '')}
				/>
			</section>
		);
	};

	const renderProcessingView = () => (
		<section className={style.processing}>
			<div className={style.processing__spinner}></div>
			<h2 className={style.processing__title}>{getString(processingPaymentLabelKey)}</h2>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString(cardErrorGenericLabelKey)} />
			<Confirm
				titleLabel={getString(tryAgainMessageLabelKey)}
				yesButton={{ label: tryAgainYesLabelKey ?? getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: tryAgainNoLabelKey ?? getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
			{errorHint && <MessageBox type='warning' dangerouslySetInnerHTML={{ __html: errorHint ? errorHint : getString('card-read-hint') }} />}
		</section>
	);

	const renderCurrentView = () => {
		console.log('Rendering view for state:', currentState, 'hasConfirmedFee:', hasConfirmedFeeRef.current);

		// If user has confirmed the fee, always show PAY view regardless of state
		if (hasConfirmedFeeRef.current && currentState !== ViewState.PROCESSING && currentState !== ViewState.ERROR) {
			return renderPayView();
		}

		switch (currentState) {
			case ViewState.SERVICE_FEE:
				clearCustomBackAction();
				return renderServiceFeeView();
			case ViewState.PAY:
				return renderPayView();
			case ViewState.PROCESSING:
				return renderProcessingView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderServiceFeeView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}

export default PaymentCard;
